/**
 * BackgroundProcessor — Processes background images with blur, darken,
 * and colour-overlay effects to produce Fabric.js-ready canvases.
 *
 * Processing pipeline:
 *   1. Crop to region (normalised 0-1 coords)
 *   2. Scale to fill target dimensions (cover fit)
 *   3. Gaussian blur via CSS filter
 *   4. Brightness adjustment
 *   5. Colour overlay
 *
 * The blur step renders the image slightly oversized and clips back to the
 * target dimensions so the semi-transparent fringe that Gaussian blur
 * produces at edges is never visible.
 *
 * @module BackgroundProcessor
 */

/**
 * @typedef {Object} CropRegion
 * @property {number} x      Left edge   (0-1)
 * @property {number} y      Top edge    (0-1)
 * @property {number} width  Region width  (0-1)
 * @property {number} height Region height (0-1)
 */

/**
 * @typedef {Object} ProcessOptions
 * @property {number}      canvasWidth   Target canvas width  (px)
 * @property {number}      canvasHeight  Target canvas height (px)
 * @property {number}      [blur=15]          Blur radius (px)
 * @property {number}      [brightness=-0.3]  Brightness adjustment (-1 to 1)
 * @property {string}      [overlay='rgba(0,0,0,0.3)'] Colour overlay
 * @property {CropRegion}  [cropRegion]  Optional crop (normalised 0-1)
 */

export class BackgroundProcessor {
  /**
   * Process an image through the full effects pipeline.
   *
   * @param {HTMLImageElement | HTMLCanvasElement} image  Source image
   * @param {ProcessOptions} options
   * @returns {HTMLCanvasElement} Ready-to-use offscreen canvas
   */
  process(image, options) {
    const {
      canvasWidth,
      canvasHeight,
      blur = 15,
      brightness = -0.3,
      overlay = 'rgba(0,0,0,0.3)',
      cropRegion,
    } = options;

    if (!canvasWidth || !canvasHeight) {
      throw new Error('canvasWidth and canvasHeight are required');
    }

    // ── 1. Resolve source dimensions & optional crop ──────────────────
    const src = this.#resolveSource(image);
    const crop = this.#resolveCrop(src, cropRegion);

    // ── 2. Cover-fit the (cropped) source into target dimensions ──────
    const coverFit = this.#coverFit(
      crop.width,
      crop.height,
      canvasWidth,
      canvasHeight
    );

    // ── 3. Create the intermediate canvas (oversized for blur bleed) ──
    // Extend by 2× blur radius on each side so the fringed edges are
    // outside the final clipping rect.
    const bleed = Math.ceil(blur * 2);
    const interW = canvasWidth + bleed * 2;
    const interH = canvasHeight + bleed * 2;

    const interCanvas = this.#createCanvas(interW, interH);
    const interCtx = interCanvas.getContext('2d');
    interCtx.imageSmoothingEnabled = true;
    interCtx.imageSmoothingQuality = 'high';

    // Build the CSS filter string
    const filterParts = [];
    if (blur > 0) filterParts.push(`blur(${blur}px)`);
    if (brightness !== 0) {
      // CSS brightness(1) = no change; 0.7 = darken by 30%
      filterParts.push(`brightness(${1 + brightness})`);
    }
    interCtx.filter = filterParts.length ? filterParts.join(' ') : 'none';

    // Draw the source into the oversized canvas, centred
    const drawW = coverFit.width + bleed * 2;
    const drawH = coverFit.height + bleed * 2;
    const drawX = (interW - drawW) / 2;
    const drawY = (interH - drawH) / 2;

    interCtx.drawImage(
      src.element,
      crop.sx,
      crop.sy,
      crop.width,
      crop.height,
      drawX,
      drawY,
      drawW,
      drawH
    );

    // ── 4. Clip to final dimensions ───────────────────────────────────
    const outCanvas = this.#createCanvas(canvasWidth, canvasHeight);
    const outCtx = outCanvas.getContext('2d');
    outCtx.imageSmoothingEnabled = true;
    outCtx.imageSmoothingQuality = 'high';

    outCtx.drawImage(
      interCanvas,
      bleed,              // source x (skip bleed area)
      bleed,              // source y
      canvasWidth,        // source width
      canvasHeight,       // source height
      0, 0,               // dest x, y
      canvasWidth,
      canvasHeight
    );

    // ── 5. Colour overlay ─────────────────────────────────────────────
    if (overlay) {
      outCtx.fillStyle = overlay;
      outCtx.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    return outCanvas;
  }

  // ── Private helpers ─────────────────────────────────────────────────

  /**
   * Normalise the source to a consistent shape.
   * @param {HTMLImageElement | HTMLCanvasElement} image
   * @returns {{ element: HTMLImageElement | HTMLCanvasElement, width: number, height: number }}
   * @private
   */
  #resolveSource(image) {
    if (image instanceof HTMLCanvasElement) {
      return { element: image, width: image.width, height: image.height };
    }
    if (image instanceof HTMLImageElement) {
      return {
        element: image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      };
    }
    throw new Error('image must be an HTMLImageElement or HTMLCanvasElement');
  }

  /**
   * Turn a normalised (0-1) crop region into pixel coordinates.
   * If no region is given, returns the full source bounds.
   * @private
   */
  #resolveCrop(src, region) {
    if (!region) {
      return { sx: 0, sy: 0, width: src.width, height: src.height };
    }

    const sx = Math.round(region.x * src.width);
    const sy = Math.round(region.y * src.height);
    const w = Math.round(region.width * src.width);
    const h = Math.round(region.height * src.height);

    return {
      sx: Math.max(0, sx),
      sy: Math.max(0, sy),
      width: Math.min(w, src.width - sx),
      height: Math.min(h, src.height - sy),
    };
  }

  /**
   * Compute dimensions for "cover" scaling (fill, no letterbox).
   * @private
   */
  #coverFit(srcW, srcH, targetW, targetH) {
    const scaleX = targetW / srcW;
    const scaleY = targetH / srcH;
    const scale = Math.max(scaleX, scaleY);

    return {
      width: Math.ceil(srcW * scale),
      height: Math.ceil(srcH * scale),
    };
  }

  /**
   * Create a blank offscreen canvas.
   * Uses OffscreenCanvas when available, otherwise a regular canvas element.
   * @private
   */
  #createCanvas(w, h) {
    // Always use a regular canvas — Fabric.js needs a standard canvas element
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
}
